/**
 * PurchaseOrderPrintView.js
 *
 * Opens a new browser window containing a fully-styled A4 Purchase Order
 * document and immediately triggers window.print().
 *
 * Usage:
 *   import { printPurchaseOrder } from './PurchaseOrderPrintView';
 *   printPurchaseOrder({ order, supplier, orgSettings, userSettings });
 */

const fmt2 = (v) =>
  v != null ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return String(v).slice(0, 10); }
};

/**
 * Build the complete HTML string for the PO document.
 */
function buildPOHtml({ order, supplier, orgName, orgAddress, orgPhone, logoUrl }) {
  const items  = order?.items || [];
  const orderNo = order?.order_number || '—';
  const createdDate = fmtDate(order?.created_at);
  const approvedDate = fmtDate(order?.approved_at);
  const status = order?.status_display || order?.status || '—';
  const dept   = order?.destination_department_name || '—';
  const createdBy = order?.created_by_name || '—';
  const approvedBy = order?.approved_by_name || '—';

  const supplierName  = supplier?.name  || items[0]?.supplier_name || '—';
  const supplierPhone = supplier?.contact_phone || '—';
  const supplierEmail = supplier?.email || '—';
  const creditDays    = supplier?.credit_duration != null ? `${supplier.credit_duration} days` : '—';

  const grossTotal = items.reduce((s, it) => {
    const gross = Number(it.pack_price || 0) * Number(it.pack_quantity || 0);
    return s + gross;
  }, 0);
  const discountTotal = grossTotal - Number(order?.total_purchase_price || grossTotal);
  const netTotal = Number(order?.total_purchase_price || grossTotal);

  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" alt="Logo" style="max-height:64px;max-width:180px;object-fit:contain;" />`
    : '';

  const rows = items.map((it, i) => {
    const packQty  = Number(it.pack_quantity  || 0);
    const upp      = Number(it.units_per_pack || 1);
    const packPr   = Number(it.pack_price     || 0);
    const freeQty  = it.units_per_pack ? Number(it.free_quantity || 0) / upp : Number(it.free_quantity || 0);
    const disc     = Number(it.purchase_discount || 0);
    const gross    = packQty * packPr;
    const net      = gross * (1 - disc / 100);
    return `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${it.product_name || '—'}</td>
        <td class="c">${packQty}</td>
        <td class="c">${upp}</td>
        <td class="c">${freeQty || 0}</td>
        <td class="r">${fmt2(packPr)}</td>
        <td class="c">${disc > 0 ? disc.toFixed(2) + '%' : '—'}</td>
        <td class="r">${fmt2(net)}</td>
        <td class="c">${it.expiry_date ? String(it.expiry_date).slice(0, 10) : '—'}</td>
      </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Purchase Order ${orderNo}</title>
  <style>
    @page { size: A4 portrait; margin: 18mm 16mm; }

    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; }

    /* ── Header ─────────────────────────────── */
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
    .org-block { flex: 1; }
    .org-name  { font-size: 16px; font-weight: 700; margin-bottom: 3px; }
    .org-sub   { font-size: 10px; color: #555; line-height: 1.5; }
    .logo-block { text-align: right; }
    .po-title   { font-size: 20px; font-weight: 800; color: #1a56db; letter-spacing: 1px; text-align: right; }
    .po-number  { font-size: 12px; font-weight: 600; text-align: right; color: #333; margin-top: 2px; }

    hr.divider { border: none; border-top: 2px solid #1a56db; margin: 8px 0; }
    hr.thin    { border: none; border-top: 1px solid #ddd; margin: 8px 0; }

    /* ── Info grid ───────────────────────────── */
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
    .info-box  { border: 1px solid #e0e0e0; border-radius: 4px; padding: 8px 10px; }
    .info-box h4 { font-size: 9px; font-weight: 700; text-transform: uppercase;
                   letter-spacing: .6px; color: #888; margin-bottom: 5px; }
    .info-row  { display: flex; justify-content: space-between; margin-bottom: 3px; }
    .info-row .lbl { color: #666; }
    .info-row .val { font-weight: 600; text-align: right; }

    /* ── Table ───────────────────────────────── */
    table { width: 100%; border-collapse: collapse; margin-bottom: 14px; font-size: 10.5px; }
    thead tr { background: #1a56db; color: #fff; }
    thead th { padding: 6px 5px; text-align: left; font-weight: 600; font-size: 10px; }
    thead th.r { text-align: right; }
    thead th.c { text-align: center; }
    tbody tr:nth-child(even) { background: #f7f9ff; }
    tbody tr td { padding: 5px 5px; border-bottom: 1px solid #eee; vertical-align: middle; }
    tbody tr td.r { text-align: right; }
    tbody tr td.c { text-align: center; }

    /* ── Totals ──────────────────────────────── */
    .totals-wrap { display: flex; justify-content: flex-end; margin-bottom: 18px; }
    .totals-table { width: 260px; }
    .totals-table tr td { padding: 4px 6px; font-size: 11px; }
    .totals-table tr td:first-child { color: #555; }
    .totals-table tr td:last-child  { text-align: right; font-weight: 600; }
    .totals-table tr.net td { font-size: 13px; font-weight: 700; color: #1a56db;
                              border-top: 2px solid #1a56db; padding-top: 6px; }

    /* ── Notes / Terms ───────────────────────── */
    .terms { border: 1px solid #e0e0e0; border-radius: 4px; padding: 8px 10px; margin-bottom: 18px; font-size: 10px; color: #555; }
    .terms h4 { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .6px; color: #888; margin-bottom: 4px; }

    /* ── Signatures ──────────────────────────── */
    .sig-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-top: 70px; }
    .sig-box  { text-align: center; }
    .sig-line { border-top: 1px solid #333; margin-bottom: 5px; padding-top: 4px; font-size: 10px; color: #555; }
    .sig-name { font-size: 9px; color: #888; }

    /* ── Footer ──────────────────────────────── */
    .footer { text-align: center; font-size: 9px; color: #aaa; margin-top: 14px; border-top: 1px solid #eee; padding-top: 6px; }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    }

    /* ── Print button (screen only) ─────────── */
    .print-bar { position: fixed; top: 0; left: 0; right: 0; background: #1a56db;
                 color: #fff; padding: 8px 16px; display: flex; align-items: center;
                 gap: 12px; z-index: 1000; font-size: 12px; box-shadow: 0 2px 6px rgba(0,0,0,.2); }
    .print-bar button { background: #fff; color: #1a56db; border: none; border-radius: 4px;
                        padding: 5px 16px; font-weight: 700; cursor: pointer; font-size: 12px; }
    .print-bar button:hover { background: #e8effe; }
    .print-spacer { height: 42px; }
  </style>
</head>
<body>

  <!-- Screen-only print bar -->
  <div class="print-bar no-print">
    <span>Purchase Order — ${orderNo}</span>
    <button onclick="window.print()">🖨 Print / Save as PDF</button>
    <button onclick="window.close()" style="background:#fee2e2;color:#b91c1c;">✕ Close</button>
  </div>
  <div class="print-spacer no-print"></div>

  <!-- ── Document body ─────────────────────── -->
  <div class="header">
    <div class="org-block">
      <div class="org-name">${orgName}</div>
      <div class="org-sub">${orgAddress ? orgAddress + '<br/>' : ''}${orgPhone ? 'Tel: ' + orgPhone : ''}</div>
    </div>
    <div class="logo-block">
      ${logoHtml}
      <div class="po-title">PURCHASE ORDER</div>
      <div class="po-number">${orderNo}</div>
    </div>
  </div>

  <hr class="divider" />

  <div class="info-grid">
    <div class="info-box">
      <h4>Supplier</h4>
      <div class="info-row"><span class="lbl">Name</span><span class="val">${supplierName}</span></div>
      <div class="info-row"><span class="lbl">Phone</span><span class="val">${supplierPhone}</span></div>
      <div class="info-row"><span class="lbl">Email</span><span class="val">${supplierEmail}</span></div>
      <div class="info-row"><span class="lbl">Credit Terms</span><span class="val">${creditDays}</span></div>
    </div>
    <div class="info-box">
      <h4>Order Details</h4>
      <div class="info-row"><span class="lbl">Order No</span><span class="val">${orderNo}</span></div>
      <div class="info-row"><span class="lbl">Date</span><span class="val">${createdDate}</span></div>
      <div class="info-row"><span class="lbl">Status</span><span class="val">${status}</span></div>
      <div class="info-row"><span class="lbl">Deliver To</span><span class="val">${dept}</span></div>
      <div class="info-row"><span class="lbl">Prepared By</span><span class="val">${createdBy}</span></div>
      ${approvedBy && approvedBy !== '—' ? `<div class="info-row"><span class="lbl">Approved By</span><span class="val">${approvedBy}</span></div>` : ''}
      ${approvedDate && approvedDate !== '—' ? `<div class="info-row"><span class="lbl">Approved On</span><span class="val">${approvedDate}</span></div>` : ''}
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th class="c" style="width:28px">#</th>
        <th>Product</th>
        <th class="c">Packs</th>
        <th class="c">Units/<br/>Pack</th>
        <th class="c">Free<br/>Packs</th>
        <th class="r">Pack Price</th>
        <th class="c">Disc.</th>
        <th class="r">Net Total</th>
        <th class="c">Expiry</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
    </tbody>
  </table>

  <div class="totals-wrap">
    <table class="totals-table">
      <tr>
        <td>Gross Total</td>
        <td>LKR ${fmt2(grossTotal)}</td>
      </tr>
      ${discountTotal > 0.005 ? `
      <tr>
        <td>Total Discount</td>
        <td>− LKR ${fmt2(discountTotal)}</td>
      </tr>` : ''}
      <tr class="net">
        <td>Net Payable</td>
        <td>LKR ${fmt2(netTotal)}</td>
      </tr>
    </table>
  </div>

  <div class="terms">
    <h4>Terms &amp; Conditions</h4>
    Please supply the above goods as per the quantities and prices stated. Kindly confirm receipt of this
    order and advise expected delivery date. All goods must be supplied with adequate expiry periods and
    in good condition. Invoice must reference the order number <strong>${orderNo}</strong>.
  </div>

  <div class="sig-grid">
    <div class="sig-box">
      <div class="sig-line">Prepared By</div>
      <div class="sig-name">${createdBy}</div>
    </div>
    <div class="sig-box">
      <div class="sig-line">Authorised By</div>
      <div class="sig-name">&nbsp;</div>
    </div>
    <div class="sig-box">
      <div class="sig-line">Supplier Acknowledgement</div>
      <div class="sig-name">&nbsp;</div>
    </div>
  </div>

  <div class="footer">
    Generated ${new Date().toLocaleString()} &nbsp;|&nbsp; ${orgName}
  </div>

</body>
</html>`;
}

/**
 * Call this to open the PO in a new window and trigger print.
 *
 * @param {object} params
 * @param {object} params.order        - MoveOrder API response object (with items array)
 * @param {object} [params.supplier]   - Full supplier object { name, contact_phone, email, credit_duration }
 */
export function printPurchaseOrder({ order, supplier }) {
  const userSettings = JSON.parse(localStorage.getItem('userSettings')       || '{}');
  const orgSettings  = JSON.parse(localStorage.getItem('organizationSettings') || '{}');

  const orgName    = userSettings.organization_name || '';
  const branding   = orgSettings?.custom_json_settings?.branding || {};
  const logoUrl    = branding.logo_url  || '';
  const orgAddress = branding.address   || '';
  const orgPhone   = branding.phone     || '';

  const html = buildPOHtml({ order, supplier, orgName, orgAddress, orgPhone, logoUrl });

  const win = window.open('', '_blank', 'width=900,height=700,scrollbars=yes');
  if (!win) {
    alert('Pop-up blocked. Please allow pop-ups for this site to print.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();

  // Wait for images (logo) to load before printing
  win.onload = () => {
    setTimeout(() => win.print(), 300);
  };
  // Fallback if onload already fired
  if (win.document.readyState === 'complete') {
    setTimeout(() => win.print(), 400);
  }
}